import { AnimatePresence, motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import React, { useContext, useEffect } from "react";
import { AuthContext } from "../../hooks/context/Context";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { sidebarItems } from "../../config/sidebarConfig";
import { hasPermission } from "../../utils/utils";
import { ReactComponent as LogoLV } from "../../assets/svg/Logo.svg";
import useResponsive from "../../hooks/useResponsive";

const SideBar = () => {
  const { menuOpen, setMenuOpen, isCollapsed, setIsCollapsed } =
    useContext(AuthContext);
  const user = useSelector(selectUserInformations);
  const { expanded, collapsed } = useResponsive();

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      isCollapsed ? `${collapsed}px` : `${expanded}px`
    );
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed, expanded, collapsed]);

  const renderNavItems = (isDesktop = true) => {
    return sidebarItems.map((item) => {
      if (item.permission && !hasPermission(user, item.permission)) return null;

      return (
        <motion.li
          key={item.to}
          className="list-none"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <NavLink
            to={item.to}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) => `
              flex items-center gap-4 p-4 rounded-xl transition-colors
              ${
                isActive
                  ? "bg-accent-dark text-primary"
                  : "text-primary hover:bg-accent"
              }
              ${isDesktop && isCollapsed ? "justify-center" : ""}
            `}
          >
            <span className="inline-block h-6 w-6 sl:h-6 sl:w-6 ll:h-7 ll:w-7">
              {React.cloneElement(item.icon, { className: "fill-current" })}
            </span>
            {(!isDesktop || !isCollapsed) && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="truncate text-md sl:text-md ll:text-lg font-[400]"
              >
                {item.label}
              </motion.span>
            )}
          </NavLink>
        </motion.li>
      );
    });
  };

  return (
    <>
      {/* Mobile Sidebar */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            />

            {/* Mobile Sidebar Content */}
            <motion.aside
              key="mobile-sidebar"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl"
            >
              <div className="flex h-20 items-center justify-between bg-primary px-6">
                <div className="flex items-center gap-3">
                  <LogoLV className="h-12 w-12 text-white" />
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg p-2 text-white hover:bg-primary-hover"
                  aria-label="Close menu"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <nav className="h-[calc(100vh-96px)] overflow-y-auto p-4 flex flex-col">
                <ul className="space-y-2 flex-1">
                  {renderNavItems(false)} {/* Pass false for mobile */}
                </ul>

                {/* Bottom Fixed Tabs - Mobile */}
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="space-y-2">
                    <button
                      onClick={() =>
                        window.open(
                          process.env.REACT_APP_FRONTEND_STREAM_URL,
                          "_blank"
                        )
                      }
                      className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors text-primary hover:bg-accent"
                    >
                      <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <span className="text-sm font-medium">LV Stream</span>
                      <svg
                        className="w-4 h-4 ml-auto text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={() =>
                        window.open(
                          process.env.REACT_APP_FRONTEND_EXAM_URL,
                          "_blank"
                        )
                      }
                      className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors text-primary hover:bg-accent"
                    >
                      <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M12 15h.01"
                          />
                        </svg>
                      </div>
                      <span className="text-sm font-medium">LV Exams</span>
                      <svg
                        className="w-4 h-4 ml-auto text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        animate={{ width: isCollapsed ? `${collapsed}px` : `${expanded}px` }}
        transition={{ type: "spring", stiffness: 300 }}
        className="fixed left-0 top-0 z-50 hidden h-screen bg-white shadow-xl lg:block"
      >
        <div className="flex h-24 sl:h-20 ll:h-24 items-center justify-center bg-primary px-2">
          <div className="flex items-center">
            <LogoLV
              className="sl:h-9 sl:w-9 ll:h-10 ll:w-10 text-white"
              onClick={() => setIsCollapsed(!isCollapsed)}
            />
          </div>
        </div>

        <nav className="h-[calc(100vh-96px)] overflow-y-auto overflow-x-hidden p-4 flex flex-col">
          <ul className="space-y-2 flex-1">{renderNavItems(true)}</ul>

          {/* Bottom Fixed Tabs - Desktop */}
          <div className="mt-auto pt-4 border-t border-gray-200">
            <div className="space-y-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() =>
                  window.open(process.env.REACT_APP_FRONTEND_STREAM_URL, "_blank")
                }
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors text-primary hover:bg-accent ${
                  isCollapsed ? "justify-center" : ""
                }`}
                title="LV Stream"
              >
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                {!isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center flex-1"
                  >
                    <span className="text-sm font-medium truncate">
                      LV Stream
                    </span>
                    <svg
                      className="w-4 h-4 ml-auto text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() =>
                  window.open(process.env.REACT_APP_FRONTEND_EXAM_URL, "_blank")
                }
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors text-primary hover:bg-accent ${
                  isCollapsed ? "justify-center" : ""
                }`}
                title="LV Exams"
              >
                <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M12 15h.01"
                    />
                  </svg>
                </div>
                {!isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center flex-1"
                  >
                    <span className="text-sm font-medium truncate">
                      LV Exams
                    </span>
                    <svg
                      className="w-4 h-4 ml-auto text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.button>
            </div>
          </div>
        </nav>
      </motion.aside>
    </>
  );
};

// const useResponsiveSidebar = () => {
//   const [width, setWidth] = useState(window.innerWidth);

//   useEffect(() => {
//     const handleResize = debounce(() => {
//       setWidth(window.innerWidth);
//     }, 100);

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const calculateWidths = () => {
//     if (width >= 1440) {
//       return { expanded: 256, collapsed: 84 };
//     }

//     if (width > 1024) {
//       const ratio = (width - 1024) / (1440 - 1024);
//       return {
//         expanded: Math.round(200 + 56 * ratio), // 200-256px
//         collapsed: Math.round(66 + 18 * ratio), // 66-84px
//       };
//     }

//     return { expanded: 256, collapsed: 84 };
//   };

//   return calculateWidths();
// };

export default SideBar;
